import React from "react";
import { observer } from "mobx-react-lite";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { getToolById } from "../components/ToolRegistry";

const PARAM_TYPE_LABELS = {
  string: "Text", number: "Number", boolean: "Boolean",
  select: "Select", file: "File", range: "Range",
};

const ToolDetail = ({ projectId }) => {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const tool = getToolById(toolId);

  if (!tool) {
    return (
      <div className="screening-empty">
        <div className="screening-empty-icon">❓</div>
        <div className="screening-empty-text">Unknown tool</div>
        <div className="screening-empty-sub">No tool found with ID "{toolId}"</div>
      </div>
    );
  }

  return (
    <div className="fadein animation-duration-500" style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <div className="screening-header">
        <span className="screening-header-icon">{tool.icon}</span>
        <span className="screening-header-title">{tool.name}</span>
        <div className="screening-header-buttons">
          <Button
            label="Use in Workflow"
            icon="pi pi-plus"
            className="screening-header-btn-primary"
            onClick={() => navigate("editor/new")}
          />
        </div>
      </div>

      <div className="screening-body">
        {/* Tool Info Card */}
        <div className="screening-tool-header-card">
          <div className="screening-tool-icon-circle" style={{ color: tool.color }}>
            {tool.icon}
          </div>
          <div className="screening-tool-info">
            <div className="screening-tool-name">{tool.name}</div>
            <div className="screening-tool-desc">{tool.description}</div>
          </div>
        </div>

        {/* Sections */}
        <div className="screening-tool-detail-sections">
          {/* Parameters */}
          <div className="screening-tool-section">
            <div className="screening-tool-section-title">📋 Parameters</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontWeight: 500 }}>Parameter</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontWeight: 500 }}>Type</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontWeight: 500 }}>Required</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontWeight: 500 }}>Default</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontWeight: 500 }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {tool.parameters.map((p, i) => (
                  <tr key={p.key} style={{ borderBottom: i < tool.parameters.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                    <td style={{ padding: "8px 12px", fontWeight: 500, color: "#1e293b" }}>{p.label}</td>
                    <td style={{ padding: "8px 12px", color: "#64748b" }}>{PARAM_TYPE_LABELS[p.type] || p.type}</td>
                    <td style={{ padding: "8px 12px" }}>{p.required ? "✅" : "—"}</td>
                    <td style={{ padding: "8px 12px", color: "#64748b", fontFamily: "monospace" }}>{p.defaultValue ?? "—"}</td>
                    <td style={{ padding: "8px 12px", color: "#64748b" }}>{p.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Ports */}
          <div className="screening-tool-section">
            <div className="screening-tool-section-title">🔌 Input / Output</div>
            <div style={{ display: "flex", gap: 24 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "#22c55e", marginBottom: 8 }}>Input Ports</div>
                {tool.inputPorts.map((port) => (
                  <div key={port.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0", fontSize: "0.8rem" }}>
                    <span style={{ background: "#f0fdf4", color: "#16a34a", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace", fontSize: "0.72rem" }}>
                      {port.dataType}
                    </span>
                    <span style={{ color: "#475569" }}>{port.label}</span>
                  </div>
                ))}
                {tool.inputPorts.length === 0 && <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>None</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "#f97316", marginBottom: 8 }}>Output Ports</div>
                {tool.outputPorts.map((port) => (
                  <div key={port.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0", fontSize: "0.8rem" }}>
                    <span style={{ background: "#fff7ed", color: "#ea580c", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace", fontSize: "0.72rem" }}>
                      {port.dataType}
                    </span>
                    <span style={{ color: "#475569" }}>{port.label}</span>
                  </div>
                ))}
                {tool.outputPorts.length === 0 && <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>None</span>}
              </div>
            </div>
          </div>

          {/* Docs */}
          <div className="screening-tool-section">
            <div className="screening-tool-section-title">📖 Documentation</div>
            <div style={{ fontSize: "0.82rem", color: "#475569", lineHeight: 1.7 }}>
              {tool.docs}
            </div>
            <div style={{ marginTop: 12, fontSize: "0.82rem", color: "#475569", lineHeight: 1.7 }}>
              <strong>Usage:</strong>
              <ol style={{ marginTop: 4, paddingLeft: 20 }}>
                <li>Click "Use in Workflow" above</li>
                <li>Connect input data to this tool node on the canvas</li>
                <li>Click the node to configure parameters</li>
                <li>Connect output to a filter or result node</li>
                <li>Run the workflow</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(ToolDetail);
